import React, { useState, useEffect } from 'react';
import { baseUrl } from '../../shared';
import dayjs from 'dayjs';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { EquipmentSelector, MobileTransducerPicker, applyOffset } from './CalibrationShared';

const authHeaders = () => ({
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});

const EMPTY_ROW = (label) => ({ label, targetNm: '', p1: '', p2: '', p3: '', p4: '', p5: '' });

const DEFAULT_TABLE = [
  EMPTY_ROW('Minimum'),
  EMPTY_ROW('20%'),
  EMPTY_ROW('60%'),
  EMPTY_ROW('Maximum'),
];

const EMPTY_FORM = {
  type: 'HYD_TW',
  technician: '',
  calibrationDate: '',
  calDueDate: '',
  temperature: '',
  humidity: '',
  passFailLimitPct: 4,
  comments: '',
};

// ── Calculation helpers ───────────────────────────────────────────────────────

// offsetNm: NM correction to add to raw readings (from portable transducer correction data)
function calcRow(row, limitPct, offsetNm) {
  const correction = parseFloat(offsetNm) || 0;
  const rawVals = [row.p1, row.p2, row.p3, row.p4, row.p5]
    .map(v => parseFloat(v))
    .filter(v => !isNaN(v));
  const target = parseFloat(row.targetNm);
  if (rawVals.length === 0 || isNaN(target) || target === 0) return null;
  const correctedVals = rawVals.map(v => v + correction);
  const avg = correctedVals.reduce((a, b) => a + b, 0) / correctedVals.length;
  const rawAvg = rawVals.reduce((a, b) => a + b, 0) / rawVals.length;
  const upper = target * (1 + limitPct / 100);
  const lower = target * (1 - limitPct / 100);
  const deviationPct = ((avg - target) / target) * 100;
  const pass = avg >= lower && avg <= upper;
  return { avg, rawAvg, correction, upper, lower, deviationPct, pass };
}

function calcOverall(asFound, asLeft, limitPct) {
  const allRows = [...asFound, ...asLeft];
  const results = allRows.map(r => calcRow(r, limitPct, 0));
  if (results.some(r => r === null)) return null; // incomplete
  return results.every(r => r.pass) ? 'PASS' : 'FAIL';
}

// ── Measurement table ─────────────────────────────────────────────────────────

// mobileOffsets: array [{psi, offsetNm}] from selected transducer — when provided, adds PSI column and shows corrected avg
function MeasurementTable({ rows, onChange, limitPct, readOnly, title, mobileOffsets }) {
  const hasMobile = !!(mobileOffsets && mobileOffsets.length > 0);

  const handleCell = (rowIdx, field) => (e) => {
    const updated = rows.map((r, i) => i === rowIdx ? { ...r, [field]: e.target.value } : r);
    onChange(updated);
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, textTransform: 'uppercase', fontSize: 11, color: 'text.secondary' }}>
        {title}
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: hasMobile ? 1000 : 800 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'var(--surface-2)' }}>
              <TableCell sx={{ width: 90, fontWeight: 600 }}>Point</TableCell>
              <TableCell sx={{ width: 90, fontWeight: 600 }}>Target (NM)</TableCell>
              {hasMobile && <TableCell sx={{ width: 80, fontWeight: 600, color: 'var(--info)' }}>PSI</TableCell>}
              {[1, 2, 3, 4, 5].map(n => (
                <TableCell key={n} sx={{ width: 75, fontWeight: 600 }}>Pass {n}</TableCell>
              ))}
              {hasMobile && <TableCell sx={{ width: 75, fontWeight: 600, color: 'text.secondary' }}>Raw Avg</TableCell>}
              <TableCell sx={{ width: 75, fontWeight: 600 }}>Avg{hasMobile ? ' (Corr)' : ''}</TableCell>
              <TableCell sx={{ width: 80, fontWeight: 600 }}>+{limitPct}%</TableCell>
              <TableCell sx={{ width: 80, fontWeight: 600 }}>-{limitPct}%</TableCell>
              <TableCell sx={{ width: 70, fontWeight: 600 }}>Dev%</TableCell>
              <TableCell sx={{ width: 60, fontWeight: 600 }}>P/F</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => {
              const psi = parseFloat(row.psi);
              const offsetNm = hasMobile && !isNaN(psi) ? applyOffset(psi, mobileOffsets) : 0;
              const calc = calcRow(row, limitPct, offsetNm);
              return (
                <TableRow key={i} sx={{ bgcolor: calc ? (calc.pass ? 'var(--pass-bg)' : 'var(--fail-bg)') : undefined }}>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {readOnly ? row.label : (
                      <TextField
                        size="small" variant="standard"
                        value={row.label}
                        onChange={handleCell(i, 'label')}
                        sx={{ width: 80 }}
                        inputProps={{ style: { fontSize: 13, fontWeight: 600 } }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {readOnly ? row.targetNm : (
                      <TextField
                        size="small" variant="standard" type="number"
                        value={row.targetNm}
                        onChange={handleCell(i, 'targetNm')}
                        sx={{ width: 75 }}
                        inputProps={{ style: { fontSize: 13 } }}
                      />
                    )}
                  </TableCell>
                  {hasMobile && (
                    <TableCell>
                      {readOnly ? (row.psi || '—') : (
                        <TextField
                          size="small" variant="standard" type="number"
                          value={row.psi || ''}
                          onChange={handleCell(i, 'psi')}
                          sx={{ width: 65 }}
                          inputProps={{ style: { fontSize: 13, color: 'var(--info)' } }}
                        />
                      )}
                    </TableCell>
                  )}
                  {['p1', 'p2', 'p3', 'p4', 'p5'].map(p => (
                    <TableCell key={p}>
                      {readOnly ? (row[p] || '—') : (
                        <TextField
                          size="small" variant="standard" type="number"
                          value={row[p]}
                          onChange={handleCell(i, p)}
                          sx={{ width: 65 }}
                          inputProps={{ style: { fontSize: 13 } }}
                        />
                      )}
                    </TableCell>
                  ))}
                  {hasMobile && (
                    <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>
                      {calc ? calc.rawAvg.toFixed(2) : '—'}
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 600 }}>
                    {calc ? calc.avg.toFixed(2) : '—'}
                    {hasMobile && calc && calc.correction !== 0 && (
                      <Typography variant="caption" color="var(--info)" sx={{ display: 'block', fontSize: 10 }}>
                        +{calc.correction.toFixed(2)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>
                    {calc ? calc.upper.toFixed(2) : '—'}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>
                    {calc ? calc.lower.toFixed(2) : '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: calc && !calc.pass ? 'error.main' : undefined }}>
                    {calc ? `${calc.deviationPct >= 0 ? '+' : ''}${calc.deviationPct.toFixed(2)}%` : '—'}
                  </TableCell>
                  <TableCell>
                    {calc
                      ? <Chip
                          label={calc.pass ? 'PASS' : 'FAIL'}
                          size="small"
                          sx={{
                            bgcolor: calc.pass ? 'var(--pass-bg)' : 'var(--fail-bg)',
                            color: calc.pass ? 'var(--pass)' : 'var(--fail)',
                            fontWeight: 700, fontSize: 11,
                          }}
                        />
                      : null
                    }
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

// ── Main form ─────────────────────────────────────────────────────────────────

export default function TorqueWrenchCalForm({ open, onClose, job, cal, canManage, onSaved }) {
  const readOnly = !canManage;
  const [form, setForm] = useState(EMPTY_FORM);
  const [asFound, setAsFound] = useState(DEFAULT_TABLE.map(r => ({ ...r })));
  const [asLeft, setAsLeft] = useState(DEFAULT_TABLE.map(r => ({ ...r })));
  const [equipmentUsed, setEquipmentUsed] = useState([]);
  const [saving, setSaving] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileTransducerId, setMobileTransducerId] = useState(null);
  const [mobileTransducer, setMobileTransducer] = useState(null);

  const mobileOffsets = isMobile && mobileTransducer && mobileTransducer.latestCorrectionData
    ? mobileTransducer.latestCorrectionData
    : null;

  useEffect(() => {
    if (!open) return;
    if (cal) {
      setForm({
        type: cal.type || 'HYD_TW',
        technician: cal.technician || '',
        calibrationDate: cal.calibrationDate ? dayjs(cal.calibrationDate).format('YYYY-MM-DD') : '',
        calDueDate: cal.calDueDate ? dayjs(cal.calDueDate).format('YYYY-MM-DD') : '',
        temperature: cal.temperature != null ? String(cal.temperature) : '',
        humidity: cal.humidity != null ? String(cal.humidity) : '',
        passFailLimitPct: cal.passFailLimitPct != null ? cal.passFailLimitPct : 4,
        comments: cal.comments || '',
      });
      const raw = cal.rawData || {};
      setAsFound(raw.asFound && raw.asFound.length ? raw.asFound : DEFAULT_TABLE.map(r => ({ ...r })));
      setAsLeft(raw.asLeft && raw.asLeft.length ? raw.asLeft : DEFAULT_TABLE.map(r => ({ ...r })));
      setEquipmentUsed(raw.equipmentSnapshot || []);
      setIsMobile(cal.subtype === 'MOBILE');
      setMobileTransducerId(raw.mobileTransducerId || null);
      setMobileTransducer(null);
    } else {
      setForm({ ...EMPTY_FORM });
      setAsFound(DEFAULT_TABLE.map(r => ({ ...r })));
      setAsLeft(DEFAULT_TABLE.map(r => ({ ...r })));
      setEquipmentUsed([]);
      setIsMobile(false);
      setMobileTransducerId(null);
      setMobileTransducer(null);
    }
  }, [open, cal]);

  // Auto-set cal due date to +12 months when cal date changes
  useEffect(() => {
    if (form.calibrationDate && !cal?.calDueDate) {
      const due = dayjs(form.calibrationDate).add(12, 'month').format('YYYY-MM-DD');
      setForm(f => ({ ...f, calDueDate: due }));
    }
  }, [form.calibrationDate]);

  // Auto-set passFailLimitPct when type changes
  useEffect(() => {
    setForm(f => ({
      ...f,
      passFailLimitPct: form.type === 'BATT_TW' ? 5 : 4,
    }));
  }, [form.type]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const limitPct = parseFloat(form.passFailLimitPct) || 4;
  const overallResult = calcOverall(asFound, asLeft, limitPct);

  // Build primaryStandard text from equipment snapshot for the cert
  const primaryStandardText = equipmentUsed.length > 0
    ? equipmentUsed.map(e =>
        `${e.name}${e.model ? ' ' + e.model : ''}${e.serialNo ? ' S/N ' + e.serialNo : ''}${e.certNo ? ' (Cal: ' + e.certNo + ')' : ''}`
      ).join('; ')
    : '';

  const handleSave = async () => {
    if (equipmentUsed.length === 0) {
      alert('Please select the test equipment used before saving.');
      return;
    }
    setSaving(true);
    try {
      const rawData = {
        asFound,
        asLeft,
        equipmentSnapshot: equipmentUsed,
        ...(isMobile && mobileTransducerId ? {
          mobileTransducerId,
          mobileTransducerName: mobileTransducer ? mobileTransducer.name + (mobileTransducer.serialNo ? ' S/N ' + mobileTransducer.serialNo : '') : '',
        } : {}),
      };
      const body = {
        ...form,
        subtype: isMobile ? 'MOBILE' : null,
        primaryStandard: primaryStandardText,
        overallResult: overallResult || null,
        rawData,
      };
      const url = cal
        ? baseUrl + 'workshop/jobs/' + job.id + '/calibrations/' + cal.id
        : baseUrl + 'workshop/jobs/' + job.id + '/calibrations';
      const method = cal ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) onSaved();
    } finally {
      setSaving(false);
    }
  };

  const title = cal
    ? `${cal.type === 'BATT_TW' ? 'Battery' : 'Hydraulic'} Torque Wrench Cal — ${cal.certNo || 'Draft'}`
    : 'New Torque Wrench Calibration';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>{title}</Typography>
          {overallResult && (
            <Chip
              label={overallResult}
              sx={{
                bgcolor: overallResult === 'PASS' ? 'var(--pass-bg)' : 'var(--fail-bg)',
                color: overallResult === 'PASS' ? 'var(--pass)' : 'var(--fail)',
                fontWeight: 700, fontSize: 14,
              }}
            />
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mb: 2 }}>

          {/* Header — Job / Cal type */}
          <Grid item xs={12}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
              Calibration Details
            </Typography>
            <Divider sx={{ mt: 0.5, mb: 1.5 }} />
          </Grid>

          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small" disabled={readOnly || !!cal}>
              <InputLabel>Type</InputLabel>
              <Select value={form.type} label="Type" onChange={set('type')}>
                <MenuItem value="HYD_TW">Hydraulic Torque Wrench</MenuItem>
                <MenuItem value="BATT_TW">Battery Torque Wrench</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              size="small" fullWidth label="Technician"
              value={form.technician} onChange={set('technician')}
              InputProps={{ readOnly }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              size="small" fullWidth label="Calibration Date" type="date"
              value={form.calibrationDate} onChange={set('calibrationDate')}
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              size="small" fullWidth label="Cal Due Date" type="date"
              value={form.calDueDate} onChange={set('calDueDate')}
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              size="small" fullWidth label="Temperature (°C)"
              type="number" value={form.temperature} onChange={set('temperature')}
              InputProps={{ readOnly }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              size="small" fullWidth label="Humidity (%)"
              type="number" value={form.humidity} onChange={set('humidity')}
              InputProps={{ readOnly }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              size="small" fullWidth label="Pass/Fail Limit %"
              type="number" value={form.passFailLimitPct} onChange={set('passFailLimitPct')}
              InputProps={{ readOnly }}
              helperText="4% Hydraulic / 5% Battery"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small" fullWidth label="Comments"
              value={form.comments} onChange={set('comments')}
              InputProps={{ readOnly }}
            />
          </Grid>

          {/* Test equipment */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
              Test Equipment Used
            </Typography>
            <Divider sx={{ mt: 0.5, mb: 1.5 }} />
          </Grid>
          <Grid item xs={12}>
            <EquipmentSelector
              selected={equipmentUsed}
              onChange={setEquipmentUsed}
              readOnly={readOnly}
            />
          </Grid>

          {/* Uncertainty summary from selected equipment */}
          {equipmentUsed.length > 0 && (() => {
            const pcts = equipmentUsed.filter(e => e.uncertaintyPct != null);
            if (!pcts.length) return null;
            const u = pcts[0];
            return (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ fontSize: 12 }}>
                  Measurement uncertainty: <strong>{u.uncertaintyPct}%</strong>
                  {u.maxErrorPct != null && <>, max error: <strong>{u.maxErrorPct}%</strong></>}
                  {u.traceability && <> — Traceability: {u.traceability}</>}
                </Alert>
              </Grid>
            );
          })()}

          {/* Mobile calibration mode */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
              Calibration Method
            </Typography>
            <Divider sx={{ mt: 0.5, mb: 1 }} />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {!readOnly && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isMobile}
                      onChange={e => setIsMobile(e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Mobile calibration (portable transducer used in field — offsets applied)</Typography>}
                />
              )}
              {readOnly && (
                <Typography variant="body2" color={isMobile ? 'primary' : 'text.secondary'}>
                  {isMobile ? 'Mobile calibration — portable transducer used' : 'Bench calibration'}
                </Typography>
              )}
              {isMobile && (
                <Box sx={{ ml: readOnly ? 0 : 3.5, maxWidth: 480 }}>
                  {readOnly
                    ? <MobileTransducerPicker value={mobileTransducerId} onChange={() => {}} readOnly />
                    : <MobileTransducerPicker
                        value={mobileTransducerId}
                        onChange={(id, t) => { setMobileTransducerId(id); setMobileTransducer(t); }}
                        readOnly={false}
                      />
                  }
                </Box>
              )}
            </Box>
          </Grid>

          {/* Measurement tables */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
              Measurement Data
            </Typography>
            <Divider sx={{ mt: 0.5, mb: 1.5 }} />
          </Grid>

          <Grid item xs={12}>
            <MeasurementTable
              title="AS FOUND (condition before service)"
              rows={asFound}
              onChange={setAsFound}
              limitPct={limitPct}
              readOnly={readOnly}
              mobileOffsets={mobileOffsets}
            />
          </Grid>

          <Grid item xs={12} sx={{ mt: 2 }}>
            <MeasurementTable
              title="AS LEFT CALIBRATED (condition after service)"
              rows={asLeft}
              onChange={setAsLeft}
              limitPct={limitPct}
              readOnly={readOnly}
              mobileOffsets={mobileOffsets}
            />
          </Grid>

          {/* Overall result */}
          {overallResult && (
            <Grid item xs={12}>
              <Box sx={{
                mt: 1, p: 2, borderRadius: 1, textAlign: 'center',
                bgcolor: overallResult === 'PASS' ? 'var(--pass-bg)' : 'var(--fail-bg)',
              }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: overallResult === 'PASS' ? 'var(--pass)' : 'var(--fail)' }}>
                  OVERALL RESULT: {overallResult}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ISO 6789-1:2017 — ±{limitPct}% tolerance
                </Typography>
              </Box>
            </Grid>
          )}
          {!overallResult && (asFound.some(r => r.targetNm) || asLeft.some(r => r.targetNm)) && (
            <Grid item xs={12}>
              <Alert severity="warning">
                Fill in all target NM values and readings to calculate the overall result.
              </Alert>
            </Grid>
          )}

        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Close</Button>
        {canManage && (
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : cal ? 'Save Changes' : 'Save Calibration'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
